import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Users } from "lucide-react";
import { ContentLayout } from "@/components/common/content-layout";
import CustomPaginationComponent from "@/components/common/custom-pagination-component";
import { DuplicateAccountsTable } from "@/features/audit/ui/duplicate-accounts-table";
import { DuplicateAccountsFilters } from "@/features/audit/ui/duplicate-accounts-filters";
import { useDuplicateAccounts } from "@/features/audit/hooks/use-duplicate-accounts";
import { UserDetailsDrawer } from "@/features/users/ui/user-details-drawer";

export default function DuplicateAccountsAudit() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page") || 0);
  const search = searchParams.get("search") || undefined;

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data, isLoading, isFetching, error } = useDuplicateAccounts({
    page,
    search,
  });

  const groups = data?.groups ?? [];
  const currentPage = data?.page ?? 0;
  const lastPage = data ? data.lastPage + 1 : 0;

  const handleUserClick = (id: string) => {
    setSelectedUserId(id);
    setIsDrawerOpen(true);
  };

  return (
    <>
      <ContentLayout
        header={
          <>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Duplicate Accounts
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Detect and merge duplicate user accounts sharing the same
                identifier.
              </p>
            </div>
            <DuplicateAccountsFilters />
          </>
        }
        data={groups}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        errorMessage="Failed to load duplicate accounts"
        emptyState={{
          message: "No duplicate accounts found",
          icon: <Users className="h-8 w-8 text-muted-foreground" />,
        }}
      >
        <DuplicateAccountsTable
          groups={groups}
          onUserClick={handleUserClick}
        />

        {groups.length > 0 && (
          <div className="mt-4">
            <CustomPaginationComponent
              currentPage={currentPage}
              lastPage={lastPage}
              onPageChange={(newPage) => {
                const params = new URLSearchParams(searchParams);
                params.set("page", newPage.toString());
                setSearchParams(params);
              }}
            />
          </div>
        )}
      </ContentLayout>

      <UserDetailsDrawer
        userId={selectedUserId}
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
      />
    </>
  );
}
